import { useRef, useState } from 'react';

import type { TableStateControl } from '../../hooks';
import type { FilterType } from '../../utils/filterSettings';
import { AdvancedFilterDialog } from '../AdvancedFilterDialog';
import { Button } from '../Button';
import { SearchButtonInput } from '../SearchButtonInput';
import { SvgIcon } from '../SvgIcon';
import { AppliedFiltersRow, type CustomFilterPills } from '../TableActionRow/AppliedFiltersRow';
import { Tooltip } from '../Tooltip';

export type MapListActionRowProps = {
  filterTypes: FilterType[];
  stateControl: TableStateControl;
  customFilterPills?: CustomFilterPills;
};

/**
 * Map Table Action Row, optimized for DRF Map view
 */
function MapListActionRow({ filterTypes, stateControl, customFilterPills }: MapListActionRowProps) {
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const addFiltersRef = useRef<HTMLButtonElement>(null);

  // Destructure stateControl
  const { globalFilter, setGlobalFilter, filters, setFilters } = stateControl;

  // Handler to remove a specific filter by index
  const handleRemoveFilter = (index: number) => {
    const newFilters = {
      ...filters,
      items: filters.items.filter((_, i) => i !== index),
    };
    setFilters(newFilters);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <SearchButtonInput
            value={globalFilter}
            onChange={setGlobalFilter}
            keepExpanded={true}
            className="h-6 w-full"
          />
          <Tooltip text="Add filters" asChild>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Add filters"
              aria-haspopup="dialog"
              aria-expanded={isFilterDialogOpen}
              ref={addFiltersRef}
              onClick={() => setIsFilterDialogOpen(true)}
              data-testid="home-map-list-open-filters"
            >
              <SvgIcon icon="filter" size={14} />
            </Button>
          </Tooltip>
        </div>

        {/* Applied Filters Row */}
        <AppliedFiltersRow
          filters={filters}
          filterTypes={filterTypes}
          handleRemoveFilter={handleRemoveFilter}
          customFilterPills={customFilterPills}
          emptyFocusRef={addFiltersRef}
        />
      </div>

      <AdvancedFilterDialog
        open={isFilterDialogOpen}
        onOpenChange={setIsFilterDialogOpen}
        filters={filters}
        onFiltersChange={setFilters}
        filterTypes={filterTypes}
      />
    </>
  );
}

export { MapListActionRow };
